import { Providers } from '@/shared/components/Providers'
import { AdminLayout } from '@/shared/layouts/AdminLayout'
import { isAuthenticated, isPublicRoute } from '@/shared/lib/auth'
import { logout, setTokenServiceClearFunction, useToken } from '@/shared/store/auth.store'
import { createRootRoute, Outlet, redirect, useRouterState } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { clearProactiveRefresh } from '@/shared/lib/tokenService'
import { Spinner } from '@workspace/ui/components/Spinner'
import { SocketProvider } from '@/shared/lib/socket/context/SocketContext'
import { SOCKET_NAMESPACES } from '@/shared/lib/socket/types'
import { NotificationSocketListener } from '@/shared/components/notifications'
import { getEnv } from '@/shared/lib/env'

export const Route = createRootRoute({
    component: RootComponent,
    beforeLoad: async ({ location }) => {
        const pathname = location.pathname

        // Allow access to public routes
        if (isPublicRoute(pathname)) {
            if (pathname === '/login' && isAuthenticated()) {
                // If already authenticated, redirect away from login page
                throw redirect({ to: '/' })
            }

            return
        }

        // Check authentication for protected routes
        if (!isAuthenticated()) {
            // Clear invalid/expired token
            await logout()

            // Redirect to login page with return URL
            throw redirect({
                to: '/login',
                search: {
                    redirect: pathname,
                },
            })
        }
    },
})

function RootComponent() {
    const router = useRouterState()
    const pathname = router.location.pathname
    const isPublicPage = isPublicRoute(pathname)
    const isNavigating = router.isLoading || router.status === 'pending'

    setTokenServiceClearFunction(() => {
        clearProactiveRefresh()
    })

    // Get token for socket connection (only used when authenticated)
    const token = useToken()

    return (
        <Providers>
            {isNavigating && (
                <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                        <Spinner className="w-10 h-10 text-primary" />
                        <span className="text-muted-foreground">Đang tải...</span>
                    </div>
                </div>
            )}

            {isPublicPage ? (
                <Outlet />
            ) : (
                <SocketProvider
                    config={{
                        url: getEnv('VITE_PMSVB_API_BASE_URL') as string,
                        options: {
                            auth: {
                                token: token || '',
                            },
                        },
                    }}
                    namespaces={[SOCKET_NAMESPACES.ROOT, SOCKET_NAMESPACES.NOTIFICATIONS]}
                >
                    <NotificationSocketListener />
                    <AdminLayout>
                        <Outlet />
                    </AdminLayout>
                </SocketProvider>
            )}
            {import.meta.env.DEV ? <TanStackRouterDevtools position="bottom-right" /> : null}
        </Providers>
    )
}
